import { isEmpty } from 'radash';
import { useCallback, useState } from 'react';

import { AUTH_OBJECT_TYPES, useAuth } from '@/hooks/useAuth';
import { useDialogHost } from '@/hooks/useDialogHost';
import { useToast } from '@/hooks/useToast';
import { useTranslation } from '@/hooks/useTranslation';
import { type BrowseToolbarRow } from '@/pages/browse/toolbar-actions';
import { categoryContext, selectedPrimaryKeys } from '@/pages/browse/toolbar-actions/toolbarSelection';
import { getInjector, getRootScope } from '@/services/angularjs-bridge';
import { fetchItemData } from '@/services/utils';

const CUSTOM_ITEM_EDITOR_SETTINGS = 'CustomItemEditorSettings';

type TemplateAttributes = Record<string, unknown>;

type CopyCompareInput = {
    selectedItems: BrowseToolbarRow[];
    allItemsSelected: boolean;
    routeCategory: string | null;
    pauseEditing?: () => void;
    /** Opens the editor on `/copy/:primaryKey`, optionally with the prior-item template values. */
    openCopy: (primaryKey: string, templateForItemCopy?: object) => void;
};

type CopyCompareAction = {
    visible: boolean;
    disabled: boolean;
    onClick: () => void;
};

function callEditorSetting(method: string, item: BrowseToolbarRow): unknown {
    try {
        const root = getRootScope(getInjector());
        if (typeof root?.callService !== 'function') {
            return null;
        }
        return root.callService(CUSTOM_ITEM_EDITOR_SETTINGS, method, item, root.user, root.organization);
    } catch {
        return null;
    }
}

/**
 * Copy and Compare above the hit list. Copy needs exactly one checked entry,
 * Compare exactly two, and global select-all disables both. Copy keeps the
 * rules of `fetch-item-controller.js#copyItem`: the `copy.items` right, create
 * and edit on the category, and the copy template pre-dialog only on the
 * template's own category.
 */
const useCopyCompareActions = ({
    selectedItems,
    allItemsSelected,
    routeCategory,
    pauseEditing,
    openCopy,
}: CopyCompareInput): { copy: CopyCompareAction; compare: CopyCompareAction } => {
    const { t } = useTranslation();
    const { auth } = useAuth();
    const { toast } = useToast();
    const dialogHost = useDialogHost();
    const [loadingCompare, setLoadingCompare] = useState(false);

    const selectedKeys = selectedPrimaryKeys({ selectedItems });
    const canCopyItemsUi = !!auth?.hasAnyPermission(AUTH_OBJECT_TYPES.OBJECT_TYPE_UI, 'copy.items');
    const canCreateAndEdit = !!auth
        && auth.hasAnyPermission(AUTH_OBJECT_TYPES.OBJECT_TYPE_ITEMS, 'create', categoryContext(routeCategory))
        && auth.hasAnyPermission(AUTH_OBJECT_TYPES.OBJECT_TYPE_ITEMS, 'edit', categoryContext(routeCategory));
    const copyDisabled = allItemsSelected || selectedKeys.length !== 1 || !canCreateAndEdit;
    const compareDisabled = allItemsSelected || selectedKeys.length !== 2 || loadingCompare;

    const onCopy = useCallback(() => {
        const item = selectedItems.find((row) => row.primaryKey__);
        const primaryKey = item?.primaryKey__;
        if (copyDisabled || !item || !primaryKey) {
            return;
        }
        pauseEditing?.();

        if (!callEditorSetting('enableCopyItemTemplate', item)) {
            openCopy(primaryKey);
            return;
        }
        const getTemplateAttributesMap = () => (
            (callEditorSetting('copyItemTemplateAttributes', item) as TemplateAttributes | null) ?? {}
        );
        const onTemplateCategory = routeCategory != null
            && routeCategory === item.category__
            && !isEmpty(getTemplateAttributesMap());
        if (!onTemplateCategory) {
            openCopy(primaryKey);
            return;
        }
        dialogHost.openPriorItem({
            dialogTitle: 'ITEM.COPY_ITEM',
            isCategorySpecific: false,
            getTemplateAttributesMap,
            // DialogHost closes the prior-item dialog in the same turn as onSubmit.
            onSubmit: (values?: object) => setTimeout(() => openCopy(primaryKey, values), 0),
        });
    }, [copyDisabled, dialogHost, openCopy, pauseEditing, routeCategory, selectedItems]);

    const onCompare = useCallback(async () => {
        if (compareDisabled) {
            return;
        }
        pauseEditing?.();
        setLoadingCompare(true);
        try {
            const items = await Promise.all(selectedKeys.map((primaryKey) => fetchItemData(primaryKey)));
            if (items.some((item) => item == null)) {
                toast.error(t('ERROR_TECHNICAL'));
                return;
            }
            dialogHost.openCompare(items, { currentLayout: 'browse' });
        } finally {
            setLoadingCompare(false);
        }
    }, [compareDisabled, dialogHost, pauseEditing, selectedKeys, t, toast]);

    return {
        copy: { visible: canCopyItemsUi, disabled: copyDisabled, onClick: onCopy },
        compare: { visible: true, disabled: compareDisabled, onClick: () => void onCompare() },
    };
};

export type { CopyCompareAction, CopyCompareInput };
export { useCopyCompareActions };
